import { useFormikEnhanced } from '@superdispatch/forms';
import { Column, Columns, useSnackbarStack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { FormikProvider } from 'formik';
import { useMemo } from 'react';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { ScrollToError } from 'shared/form/ScrollToError';
import {
  setDriverPreferencesVehicleDetails,
  useDriverPreferences,
  useDriverPreferencesCacheUpdater,
  useDriverPreferencesHistoricalData,
} from 'shared/modules/driver/DriverPreferencesAPI';
import {
  DriverPreferencesVehicleDetailsDTO,
  driverPreferencesVehicleDetailsSchema,
} from 'shared/modules/driver/DriverPreferencesDTO';
import { trackDriverPreferences } from '../DriverPreferencesAnalytics';
import {
  DriverPreferencesStepItem,
  useDriverPreferencesStepperContext,
} from '../DriverPreferencesStepper';
import { useVehicleTypeItems } from '../VehicleTypeItems';
import { VehicleDetailsStepContent } from './VehicleDetailsStepContent';

export function VehicleDetailsStep() {
  const { handlePrevious } = useDriverPreferencesStepperContext();
  const { data: driverPreferences } = useDriverPreferences();
  const { data: historicalData } = useDriverPreferencesHistoricalData({
    enabled:
      !!driverPreferences && driverPreferences.onboarding_step !== 'completed',
  });
  const { addSnackbar } = useSnackbarStack();

  const vehicleDetailsPreferences = useMemo(() => {
    if (historicalData) {
      return driverPreferencesVehicleDetailsSchema.cast({
        ...driverPreferences,
        ...historicalData,
      });
    }
    return driverPreferencesVehicleDetailsSchema.cast(driverPreferences);
  }, [driverPreferences, historicalData]);

  const updateCache = useDriverPreferencesCacheUpdater();

  const vehicleTypeItems = useVehicleTypeItems(driverPreferences);

  const formik = useFormikEnhanced<
    DriverPreferencesVehicleDetailsDTO,
    APIResponse<DriverPreferencesVehicleDetailsDTO>
  >({
    initialValues: vehicleDetailsPreferences,
    validationSchema: driverPreferencesVehicleDetailsSchema,
    onSubmit: (values) => {
      const destructedValues = { ...values };
      const vehicleTypeNames = vehicleTypeItems.map((vehicle) => {
        return vehicle.name;
      });

      destructedValues.preferred_vehicle_types =
        destructedValues.preferred_vehicle_types?.filter(
          (preferredVehicle: string) => {
            return vehicleTypeNames.includes(preferredVehicle);
          },
        );
      return setDriverPreferencesVehicleDetails(destructedValues);
    },
    onSubmitSuccess: (response, _) => {
      trackDriverPreferences({
        name: 'Driver Completed Step 3 Vehicle Details',
      });
      updateCache((cache) => {
        return { ...cache, ...response.data, onboarding_step: 'completed' };
      });
      MobileAppBridge.closeScreen();
    },
    onSubmitFailure(error) {
      addSnackbar(error.message || 'Failed to save preferences', {
        variant: 'error',
      });
    },
  });

  const { isSubmitting } = formik;

  return (
    <FormikProvider value={formik}>
      <ScrollToError />
      <DriverPreferencesStepItem
        actions={
          <Columns space="small">
            <Column width="1/2">
              <Button
                fullWidth={true}
                variant="neutral"
                onClick={handlePrevious}
              >
                Back
              </Button>
            </Column>
            <Column width="1/2">
              <Button
                type="submit"
                pending={isSubmitting}
                fullWidth={true}
                onClick={() => {
                  void formik.submitForm();
                }}
              >
                Confirm
              </Button>
            </Column>
          </Columns>
        }
      >
        <VehicleDetailsStepContent />
      </DriverPreferencesStepItem>
    </FormikProvider>
  );
}
